import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  getAccessToken,
  getAuthCookieDomain,
  getRefreshToken,
  mayUseTgSession,
  writeAuthTokens,
} from '@/shared/session/auth';
import { logger } from '@/shared/libs/logger';
import logout from './logout';
import { hasAccessLimitationStarted, redirectToAccessLimitation } from './session-redirect';
import { apiUri, externalConnections } from '@/constants/Constants';

type FailedRequest = {
  reject: (reason?: unknown) => void;
  resolve: (value: unknown) => void;
};

const CSRF_HEADER_NAME = 'X-Requested-With';
const SAFE_METHODS = new Set(['get', 'head', 'options']);
// Unauthenticated endpoints: they never need the session cookie, and their CORS may not allow it yet. Everything
// under public/ is the Node BFF's public router; the Go BFF's public routes are listed.
const PUBLIC_API_PATHS = ['public/', apiUri.register, apiUri.registerBrokerModern];

const isPublicRequest = (url: string | undefined) =>
  PUBLIC_API_PATHS.some((path) => url?.replace(/^\//, '').startsWith(path));

let failedQueue: FailedRequest[] = [];
let isRefreshing = false;

const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });

  failedQueue = [];
};

/**
 * A browser signed in through the new login holds only the HttpOnly TG session cookie, which the BFFs read. It
 * has no token to send, refresh or revoke. A 401 logs out: that ends the Auth0 session too, so a user who isn't
 * signed in always lands on the login form, and signing in can't silently return to a browser the BFFs keep
 * rejecting. A 403 on /me, such as for a renter's session, shows the access message. Any other error goes back to
 * the caller.
 */
function endTgSession(error: AxiosError): Promise<never> | undefined {
  const status = error.response?.status;
  const isMeRequest = error.config?.url?.replace(/^\//, '') === apiUri.getMe;

  if (status === 401) {
    if (!hasAccessLimitationStarted()) {
      void logout({ reason: 'auth_error', context: { status, tgSession: true } });
    }
    return leavingPage();
  }
  if (status === 403 && isMeRequest) {
    redirectToAccessLimitation({ status });
    return leavingPage();
  }
  return undefined;
}

// Never settles: the page is leaving, and a rejection would let callers render error states on the way out.
const leavingPage = () => new Promise<never>(() => {});

export function createAxiosClient({ options }: { options: AxiosRequestConfig }) {
  const client = axios.create(options);
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else if (mayUseTgSession() && !isPublicRequest(config.url)) {
        // Legacy requests stay as they were. Without a token, the request carries the TG session cookie, and the
        // header the BFFs require on state-changing requests authenticated by it, against CSRF.
        config.withCredentials = true;
        if (!SAFE_METHODS.has((config.method ?? 'get').toLowerCase())) {
          config.headers[CSRF_HEADER_NAME] = 'XMLHttpRequest';
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    // Any status code that lie within the range of 2xx cause this function to trigger
    (response) => {
      return response;
    },
    // Existing refresh behavior is intentionally preserved during the architecture migration.
    // eslint-disable-next-line complexity
    async (error: AxiosError<{ message?: string }>) => {
      if (mayUseTgSession()) {
        const ending = endTgSession(error);
        if (ending) {
          return ending;
        }
        throw error;
      }

      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      // In "axios": "^1.1.3" there is an issue with headers, and this is the workaround.
      // originalRequest.headers = JSON.parse(JSON.stringify(originalRequest.headers || {}));
      const refreshToken = getRefreshToken();
      // If error, process all the requests in the queue and logout the user.
      const handleError = (refreshError: unknown) => {
        processQueue(refreshError);
        logout({ reason: 'auth_error', context: { status: error.response?.status } });
        // Never settles: the page is leaving for login, and a rejection would let callers
        // render error states on the way out.
        return new Promise<never>(() => {});
      };

      // Refresh token conditions
      if (
        refreshToken &&
        error.response?.status === 401 &&
        error.response.data.message === 'Unauthorized' &&
        originalRequest?.url !== externalConnections.refreshTokenUrl &&
        originalRequest?._retry !== true
      ) {
        if (isRefreshing) {
          try {
            await new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            });
            return await client(originalRequest);
          } catch (err) {
            return await Promise.reject(err);
          }
        }
        isRefreshing = true;
        originalRequest._retry = true;
        return await axios({
          url: externalConnections.refreshTokenUrl,
          data: { refresh_token: refreshToken },
          method: 'POST',
        })
          .then((res) => {
            // One write, so a reload can never restore a mismatched pair. A
            // rejected cookie leaves the stale token in place, so the retry
            // below 401s into a logout — record why before that happens.
            const rotated = {
              access_token: res.data?.access_token,
              refresh_token: res.data?.refresh_token,
            };
            if (!writeAuthTokens(rotated)) {
              logger.error('Auth cookie not stored on token refresh', {
                cookieDomain: getAuthCookieDomain(),
                // Separates a malformed refresh response from a browser
                // rejection, without logging the tokens themselves.
                responseHadTokens: Boolean(rotated.access_token && rotated.refresh_token),
              });
            }
            processQueue(null);

            return client(originalRequest);
          }, handleError)
          .finally(() => {
            isRefreshing = false;
          });
      }

      // Refresh token missing or expired => logout user...
      if (
        error.response?.status === 401 ||
        error.response?.data?.message === 'Unauthorized' ||
        error.response?.status === 403
      ) {
        return handleError(error);
      }

      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      throw error;
    },
  );

  return client;
}
